// src/api/client.ts

import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from "axios";

export const BASE_URL = (
  (window as any).__ENV__?.REACT_APP_API_URL ||
  process.env.REACT_APP_API_URL ||
  "http://localhost:8080"
).replace(/\/+$/, "");

// Claves de sesión del panel en localStorage
export const TOKEN_KEY = "tp_admin_token";
export const REFRESH_KEY = "tp_admin_refresh";
export const USER_KEY = "tp_admin_user";

const client = axios.create({
  baseURL: BASE_URL,
  timeout: 20000,
  headers: { "Content-Type": "application/json" },
});

export function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
}

function redirectToLogin() {
  clearSession();
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
}

// ── Renovación del token de Firebase ─────────────────────────────────────────
// El idToken de Firebase dura 1 hora. Antes de cada petición revisamos cuánto
// le queda y, si faltan menos de 5 minutos, lo renovamos con el refreshToken.
// Una sola renovación a la vez aunque salgan varias peticiones en paralelo.
const REFRESH_MARGIN_MS = 5 * 60 * 1000;
let refreshing: Promise<string> | null = null;

function tokenExpiresAt(token: string): number | null {
  try {
    const payload = JSON.parse(
      atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")),
    );
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

async function refreshToken(): Promise<string> {
  const refresh = localStorage.getItem(REFRESH_KEY);
  if (!refresh) throw new Error("Sin refreshToken");
  const res = await axios.post(`${BASE_URL}/auth/refresh-token`, {
    refreshToken: refresh,
  });
  const { idToken, refreshToken: newRefresh } = res.data ?? {};
  if (!idToken) throw new Error("Respuesta de renovación inválida");
  localStorage.setItem(TOKEN_KEY, idToken);
  if (newRefresh) localStorage.setItem(REFRESH_KEY, newRefresh);
  return idToken;
}

async function getValidToken(): Promise<string | null> {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return null;
  const exp = tokenExpiresAt(token);
  if (exp && exp - Date.now() > REFRESH_MARGIN_MS) return token;

  if (!refreshing) {
    refreshing = refreshToken().finally(() => {
      refreshing = null;
    });
  }
  return refreshing;
}

// ── Request interceptor: adjunta el token vigente ────────────────────────────
client.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  try {
    const token = await getValidToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
  } catch {
    // No se pudo renovar: la sesión venció
    redirectToLogin();
    throw new axios.Cancel("Sesión vencida");
  }
  return config;
});

// ── Response interceptor: 401 → volver al login ──────────────────────────────
client.interceptors.response.use(
  (res: AxiosResponse) => res,
  (error: AxiosError<any>) => {
    if (error.response?.status === 401) {
      redirectToLogin();
    }
    return Promise.reject(error);
  },
);

export default client;
